"use client";

import React from "react";
import Reveal from "@/components/common/Reveal";
import { SlidersHorizontal, ArrowRight, ShieldCheck, CircleCheck as CheckCircle2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
interface HeroCopy {
  eyebrowCat: string;
  eyebrowStd: string;
  h1: string;
  lead: string;
  wedgeBtn: string;
  imgAlt: string;
  imgBadge: string;
  footerLeft: string;
  footerRight: string;
  slotRangeLabel: string;
  slotRangeVal: string;
  wovenRangeLabel: string;
  wovenRangeVal: string;
  materialsLabel: string;
  materialsVal: string;
  standardsLabel: string;
  standardsVal: string;
}
const HERO_COPY: Record<Lang, HeroCopy> = {
  en: {
    eyebrowCat: "Screening & Separation",
    eyebrowStd: "ISO 9044 / ASTM E2016",
    h1: "Precision Mesh Engineering for Screening & High-Efficiency Separation",
    lead: "Engineered stainless steel woven wire mesh, wedge wire screens, and custom screen fabrications tailored for demanding particle sizing, liquid-solid separation, and extreme industrial process environments.",
    wedgeBtn: "Explore Screening Types",
    imgAlt: "Precision woven stainless steel screening mesh for industrial particle separation",
    imgBadge: "Industrial Screen Matrix",
    footerLeft: "Rolls, sieve bends & screen cylinders",
    footerRight: "OEM Custom",
    slotRangeLabel: "Wedge Slot Range",
    slotRangeVal: "0.02 mm – 5.0 mm",
    wovenRangeLabel: "Woven Mesh Range",
    wovenRangeVal: "1 – 635 Mesh (1 µm – 25 mm)",
    materialsLabel: "Standard Alloys",
    materialsVal: "AISI 304 / 316L / 314",
    standardsLabel: "Manufacturing Standards",
    standardsVal: "ISO 9044 / ASTM E2016"
  },
  zh: {
    eyebrowCat: "筛分与分离",
    eyebrowStd: "ISO 9044 / ASTM E2016",
    h1: "精密筛网工程：工业筛分与高效分离解决方案",
    lead: "工程级不锈钢编织丝网、楔形网构件与定制筛网组件，专为苛刻工况下的颗粒精密分级、固液高效分离及耐磨蚀流体工艺设计，支持定制孔型与工况合金配置。",
    wedgeBtn: "浏览筛分与分离类型",
    imgAlt: "用于工业颗粒分级与固液分离的高精度不锈钢筛网",
    imgBadge: "工业精密筛网矩阵",
    footerLeft: "卷料、弧形筛板与筛筒组件",
    footerRight: "OEM定制",
    slotRangeLabel: "楔形网缝隙范围",
    slotRangeVal: "0.02 mm – 5.0 mm",
    wovenRangeLabel: "编织筛网规格",
    wovenRangeVal: "1 – 635 目 (1 µm – 25 mm)",
    materialsLabel: "标准材质牌号",
    materialsVal: "AISI 304 / 316L / 314",
    standardsLabel: "制造执行标准",
    standardsVal: "ISO 9044 / ASTM E2016"
  },
  es: {
    eyebrowCat: "Tamizado y separación",
    eyebrowStd: "ISO 9044 / ASTM E2016",
    h1: "Ingeniería de mallas de precisión para tamizado y separación eficiente",
    lead: "Mallas metálicas tejidas de acero inoxidable, cribas de alambre en cuña y componentes a medida diseñados para clasificación exigente de partículas y separación sólido-líquido.",
    wedgeBtn: "Explorar tipos de separación",
    imgAlt: "Malla de acero inoxidable de precisión para tamizado y separación industrial",
    imgBadge: "Matriz de cribado industrial",
    footerLeft: "Rollos, paneles curvos y cilindros",
    footerRight: "OEM personalizado",
    slotRangeLabel: "Ranura de alambre en cuña",
    slotRangeVal: "0,02 mm – 5,0 mm",
    wovenRangeLabel: "Rango de malla tejida",
    wovenRangeVal: "1 – 635 Mesh (1 µm – 25 mm)",
    materialsLabel: "Aleaciones estándar",
    materialsVal: "AISI 304 / 316L / 314",
    standardsLabel: "Normas de fabricación",
    standardsVal: "ISO 9044 / ASTM E2016"
  },
  de: {
    eyebrowCat: "Siebung & Trennung",
    eyebrowStd: "ISO 9044 / ASTM E2016",
    h1: "Präzisions-Siebgewebe für industrielle Siebung & Hocheffizienz-Trennung",
    lead: "Hochwertiges Edelstahl-Drahtgewebe, Spaltsiebe und maßgeschneiderte Siebkomponenten für anspruchsvolle Partikelklassierung, Fest-Flüssig-Trennung und industrielle Prozesse.",
    wedgeBtn: "Abscheideverfahren erkunden",
    imgAlt: "Präzisions-Edelstahlsiebgewebe für industrielle Partikeltrennung",
    imgBadge: "Industrielle Siebmatrix",
    footerLeft: "Rollen, Bogensiebe & Siebzylinder",
    footerRight: "OEM-Kundenspezifisch",
    slotRangeLabel: "Spaltweite (Spaltsieb)",
    slotRangeVal: "0,02 mm – 5,0 mm",
    wovenRangeLabel: "Drahtgewebebereich",
    wovenRangeVal: "1 – 635 Mesh (1 µm – 25 mm)",
    materialsLabel: "Standardlegierungen",
    materialsVal: "AISI 304 / 316L / 314",
    standardsLabel: "Fertigungsnormen",
    standardsVal: "ISO 9044 / ASTM E2016"
  },
  fr: {
    eyebrowCat: "Criblage & séparation",
    eyebrowStd: "ISO 9044 / ASTM E2016",
    h1: "Ingénierie de toiles de précision pour le criblage et la séparation",
    lead: "Toiles métalliques tissées en acier inoxydable, grilles à fil profilé et composants de tamisage sur mesure pour la séparation solide-liquide et la classification de particules.",
    wedgeBtn: "Explorer les types de criblage",
    imgAlt: "Toile métallique de criblage en acier inoxydable pour séparation industrielle",
    imgBadge: "Matrice de criblage industriel",
    footerLeft: "Rouleaux, grilles courbes & cylindres",
    footerRight: "OEM sur mesure",
    slotRangeLabel: "Ouverture de fente",
    slotRangeVal: "0,02 mm – 5,0 mm",
    wovenRangeLabel: "Gamme de toile tissée",
    wovenRangeVal: "1 – 635 Mesh (1 µm – 25 mm)",
    materialsLabel: "Alliages standard",
    materialsVal: "AISI 304 / 316L / 314",
    standardsLabel: "Normes de fabrication",
    standardsVal: "ISO 9044 / ASTM E2016"
  },
  pt: {
    eyebrowCat: "Peneiramento & separação",
    eyebrowStd: "ISO 9044 / ASTM E2016",
    h1: "Engenharia de telas de precisão para peneiramento e separação eficiente",
    lead: "Telas de arame tecidas de aço inoxidável, telas de arame perfilado em cunha e componentes sob medida projetados para classificação de partículas e separação sólido-líquido.",
    wedgeBtn: "Explorar tipos de separação",
    imgAlt: "Tela de arame de precisão para peneiramento e separação industrial",
    imgBadge: "Matriz de telas industriais",
    footerLeft: "Rolos, telas curvas e cilindros",
    footerRight: "OEM personalizado",
    slotRangeLabel: "Abertura de ranhura",
    slotRangeVal: "0,02 mm – 5,0 mm",
    wovenRangeLabel: "Faixa de tela tecida",
    wovenRangeVal: "1 – 635 Mesh (1 µm – 25 mm)",
    materialsLabel: "Ligas padrão",
    materialsVal: "AISI 304 / 316L / 314",
    standardsLabel: "Normas de fabricação",
    standardsVal: "ISO 9044 / ASTM E2016"
  }
};
export default function ScreeningHero() {
  const {
    lang
  } = useLanguage();
  const c = pick(lang, HERO_COPY);
  const technicalHighlights = [{
    label: c.slotRangeLabel,
    val: c.slotRangeVal
  }, {
    label: c.wovenRangeLabel,
    val: c.wovenRangeVal
  }, {
    label: c.materialsLabel,
    val: c.materialsVal
  }, {
    label: c.standardsLabel,
    val: c.standardsVal
  }];
  return <section id="screeninghero" data-nav="light" className="relative bg-background text-foreground mf-hero border-b border-border">
      <div className="mf-container relative">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12">
          {/* Left Column (7 cols on lg) */}
          <Reveal className="lg:col-span-7 flex flex-col items-start text-left">
            {/* Eyebrow Badges */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-muted px-3 py-1 text-[13px] font-mono tracking-tight text-foreground">
                <SlidersHorizontal className="size-3.5 text-primary" />
                <span>{c.eyebrowCat}</span>
              </Badge>
              <Badge variant="outline" className="rounded-md border-border bg-muted px-2.5 py-1 text-[13px] font-mono text-foreground">
                <ShieldCheck className="mr-1 size-3 text-primary inline" />
                <span>{c.eyebrowStd}</span>
              </Badge>
            </div>

            {/* Main Headline */}
            <h1 className="font-default text-balance mf-h1 text-foreground mb-4">
              {c.h1}
            </h1>

            {/* Supporting Paragraph */}
            <p className="max-w-2xl text-muted-foreground mf-lead mb-6">
              {c.lead}
            </p>

            {/* 2 CTAs */}
            <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
              <Button size="lg" asChild className="px-6 text-sm font-semibold transition-colors w-full sm:w-auto">
                <Link to="/RequestAQuotePage" className="inline-flex items-center justify-center gap-2">
                  <span>{pick(lang, COMMON.requestQuote)}</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button variant="outline" size="lg" asChild className="px-6 text-sm font-semibold transition-colors w-full sm:w-auto">
                <Link to="/PowderAndParticleScreeningPage" className="inline-flex items-center justify-center gap-2">
                  <span>{c.wedgeBtn}</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </Reveal>

          {/* Right Column: Visual Spec Panel (5 cols on lg) */}
          <Reveal delay={100} className="lg:col-span-5 w-full">
            <Card className="overflow-hidden rounded-lg border border-border bg-card text-card-foreground gap-0 py-0">
              {/* Product Visual Container */}
              <div className="mf-card-media mf-ratio-hero relative w-full overflow-hidden">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-be8fed.png" alt={c.imgAlt} loading="eager" fetchPriority="high" decoding="async" className="w-full h-full object-cover" />
                <div className="absolute top-3 left-3">
                  <Badge className="bg-card text-foreground border border-border px-2.5 py-1 text-[13px] font-semibold rounded-md flex items-center gap-1.5">
                    <CheckCircle2 className="size-3.5 text-primary" />
                    <span>{c.imgBadge}</span>
                  </Badge>
                </div>
              </div>

              {/* Single Footer Row */}
              <CardContent className="p-4 bg-card">
                <div className="flex items-center justify-between text-[13px]">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                    <span>{c.footerLeft}</span>
                  </div>
                  <Link to="/OEMCustomMeshEngineeringPage" className="font-medium text-primary hover:underline whitespace-nowrap ml-2 flex items-center gap-1 shrink-0">
                    <span>{c.footerRight}</span>
                    <ArrowRight className="size-3" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </Reveal>
        </div>

        {/* Spec Strip Below Grid */}
        <div className="mf-spec-strip">
          {technicalHighlights.map((spec, idx) => <div key={idx} className="mf-spec-strip__item">
              <span className="mf-spec-strip__label">{spec.label}</span>
              <span className="mf-spec-strip__value">{spec.val}</span>
            </div>)}
        </div>
      </div>
    </section>;
}